"use client";

import { useState, useEffect, useRef } from "react";
import { Heart, Wallet, Gift } from "lucide-react";
import type { DonationBoxConfig } from "./DonationBoxEditor";
import { ALL_PAYMENT_METHOD_IDS, PAYMENT_METHOD_LABELS, HIDDEN_PAYMENT_METHOD_IDS } from "./PaymentFlow";

const METHOD_LOGOS: Record<string, React.ReactNode> = {
  "card-bank": (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-0.5">
        <img src="/Visa-logo.png" alt="Visa" className="h-3 rounded bg-white px-0.5" />
        <img src="/mastercard-logo.png" alt="MC" className="h-3 rounded" />
        <img src="/amex.png" alt="Amex" className="h-3.5 rounded bg-white px-0.5" />
        <img src="/discover.jpg" alt="Discover" className="h-3 rounded bg-white px-0.5" />
      </div>
      <div className="flex items-center gap-0.5 text-[8px] text-gray-500">
        <span>Powered by</span>
        <img src="/Stripe_Logo.png" alt="Stripe" className="h-2.5 rounded-full" />
      </div>
    </div>
  ),
  "coinbase-onramp": (
    <div className="flex items-center gap-0.5">
      <img src="/Visa-logo.png" alt="Visa" className="h-3 rounded bg-white px-0.5" />
      <img src="/mastercard-logo.png" alt="MC" className="h-3 rounded bg-white px-0.5" />
      <img src="/applepay-logo.png" alt="Apple Pay" className="h-3 rounded bg-white px-0.5" />
      <img src="/googlepay-logo.png" alt="Google Pay" className="h-3 rounded bg-white px-0.5" />
    </div>
  ),
  "cashapp": (
    <div className="flex items-center gap-0.5">
      <img src="/CashApp White BG.png" alt="CashApp" className="h-4 w-4 rounded-full" />
      <img src="/venmo.png" alt="Venmo" className="h-3.5 w-3.5 rounded-full bg-white p-0.5" />
      <img src="/zelle.png" alt="Zelle" className="h-3.5 w-3.5 rounded-full bg-white p-0.5" />
      <img src="/revoult.png" alt="Revolut" className="h-3.5 w-3.5 rounded-full bg-white p-0.5" />
      <img src="/wise.png" alt="Wise" className="h-3.5 w-3.5 rounded-full bg-white/90 p-0.5" />
    </div>
  ),
  "wallet": (
    <div className="flex items-center justify-center w-5 h-5 rounded bg-[#61CE70]/15">
      <Wallet size={10} className="text-[#61CE70]" />
    </div>
  ),
};

// Per-method label overrides for the preview rows. The Coinbase card now leads
// with its card-network logos, so its row reads "Instant and secure payment"
// instead of the bare "Coinbase" label used elsewhere in the app.
const METHOD_LABEL_OVERRIDES: Record<string, string> = {
  "coinbase-onramp": "Instant and secure payment",
};

type PageType = "donation" | "request" | "coffee";

interface DonationPreviewProps {
  config: DonationBoxConfig;
  ownerUsername: string;
  ownerPhotoURL?: string | null;
  pageType?: PageType;
  disabledPaymentMethods?: string[];
}

const PAGE_DEFAULTS: Record<PageType, { icon: typeof Heart; iconColor: string; defaultTitle: (name: string) => string; defaultDesc: string; defaultButton: string; defaultColor: string }> = {
  donation: {
    icon: Heart,
    iconColor: "#EC4899",
    defaultTitle: (name) => `Support ${name || "Creator"}`,
    defaultDesc: "Your donation makes a difference",
    defaultButton: "Donate Now",
    defaultColor: "#EC4899",
  },
  request: {
    icon: Wallet,
    iconColor: "#61CE70",
    defaultTitle: (name) => `Pay ${name || "Creator"}`,
    defaultDesc: "Send a payment securely via CashTap",
    defaultButton: "Send Payment",
    defaultColor: "#61CE70",
  },
  coffee: {
    icon: Gift,
    iconColor: "#FBBF24",
    defaultTitle: (name) => `Buy ${name || "Creator"} a Coffee`,
    defaultDesc: "Show your appreciation with a quick tip!",
    defaultButton: "Send Tip",
    defaultColor: "#FBBF24",
  },
};

export default function DonationPreview({
  config,
  ownerUsername,
  ownerPhotoURL,
  pageType = "donation",
  disabledPaymentMethods = [],
}: DonationPreviewProps) {
  const [carouselIdx, setCarouselIdx] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const imageLayout = config.imageLayout || "grid";
  const defaults = PAGE_DEFAULTS[pageType];
  const IconComponent = defaults.icon;
  const color = config.primaryColor || defaults.defaultColor;
  const title = config.title || defaults.defaultTitle(ownerUsername);
  const desc = config.description || defaults.defaultDesc;
  const btnText = config.buttonText || defaults.defaultButton;

  useEffect(() => {
    if (imageLayout === "carousel" && config.images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCarouselIdx((prev) => (prev + 1) % config.images.length);
      }, 3000);
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [imageLayout, config.images.length]);

  const bgStyle = config.backgroundImage
    ? {
        backgroundImage: `linear-gradient(to bottom, rgba(9,13,20,0.85), rgba(9,13,20,0.95)), url(${config.backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : {};

  // Shared: Pay button
  const payButton = () => (
    <div
      className="text-center py-3.5 rounded-2xl text-sm font-semibold text-white shadow-lg"
      style={{ backgroundColor: color }}
    >
      {btnText}
    </div>
  );

  // Shared: Thank you message
  const thankYou = () =>
    config.thankYouMessage ? (
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-4 text-center">
        <IconComponent size={18} className="mx-auto mb-1.5" style={{ color }} />
        <p className="text-gray-300 text-sm italic line-clamp-2">{config.thankYouMessage}</p>
      </div>
    ) : null;

  // ── Facebook-profile-style layout ──
  const profileContent = () => {
    const coverImage = config.backgroundImage || (config.images.length > 0 ? config.images[0] : null);
    const galleryImages = config.backgroundImage ? config.images : config.images.slice(1);

    return (
      <>
        {/* Cover image */}
        <div className="relative">
          <div className="overflow-hidden">
            {coverImage ? (
              <div className="relative">
                <img src={coverImage} alt="Cover" className="w-full h-36 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#090D14] via-[#090D14]/40 to-transparent" />
              </div>
            ) : (
              <div className="w-full h-36 bg-gradient-to-br from-[#0F1419] to-[#1A1F2B]" />
            )}
          </div>

          {/* Avatar — bottom center, overlapping */}
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 z-10">
            <div
              className="w-20 h-20 rounded-full overflow-hidden border-[3px] border-[#090D14] shadow-lg"
            >
              {ownerPhotoURL ? (
                <img src={ownerPhotoURL} alt={ownerUsername} className="w-full h-full object-cover" />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${color}33, ${color}11)` }}
                >
                  <IconComponent size={28} style={{ color }} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Spacer for overlapping avatar */}
        <div className="h-12" />

        {/* Title & Description — centered */}
        <div className="text-center px-5 mb-4">
          <h2 className="text-white font-bold text-lg mb-1.5">{title}</h2>
          <p className="text-gray-400 text-sm leading-relaxed line-clamp-3 max-w-[320px] mx-auto">{desc}</p>
        </div>

        {/* Gallery — remaining images */}
        {galleryImages.length > 0 && (
          <div className="mx-5 mb-4 bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-2xl p-3">
            {(() => {
              if (imageLayout === "carousel" && galleryImages.length > 1) {
                return (
                  <div>
                    <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-gray-700/30">
                      {galleryImages.map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`Gallery ${idx + 1}`}
                          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                            idx === carouselIdx % galleryImages.length ? "opacity-100" : "opacity-0"
                          }`}
                        />
                      ))}
                    </div>
                    <div className="flex justify-center gap-1.5 mt-2.5">
                      {galleryImages.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCarouselIdx(idx)}
                          className={`w-1.5 h-1.5 rounded-full transition-all ${
                            idx === carouselIdx % galleryImages.length ? "w-4 bg-white" : "bg-gray-600"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <div className={`grid gap-2.5 ${galleryImages.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                  {galleryImages.slice(0, 4).map((img, idx) => (
                    <div key={idx} className="aspect-square rounded-xl overflow-hidden border border-gray-700/30">
                      <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Amount */}
        <div className="mx-5 mb-4">
          <div className="flex items-center justify-between bg-gray-800/30 border border-gray-700/50 rounded-2xl px-4 py-3">
            <span className="text-gray-400 text-sm">Amount</span>
            <span className="text-white font-bold text-lg">$0.00</span>
          </div>
        </div>

        {/* Select Payment Method */}
        <div className="mx-5 mb-4">
          <p className="text-white font-semibold text-sm mb-2.5">Select Payment Method</p>
          <div className="space-y-1.5">
            {ALL_PAYMENT_METHOD_IDS
              .filter((id) => id !== "card-bank") // Stripe not available on collect pages
              .filter((id) => !HIDDEN_PAYMENT_METHOD_IDS.includes(id)) // peer extension down
              .filter((id) => !disabledPaymentMethods.includes(id))
              .map((id, idx) => (
                <div
                  key={id}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all ${
                    idx === 0
                      ? "border-[#61CE70]/50 bg-[#61CE70]/5"
                      : "border-gray-700/40 bg-gray-800/20"
                  }`}
                >
                  <div className="shrink-0">{METHOD_LOGOS[id]}</div>
                  <span className={`text-xs font-medium ${idx === 0 ? "text-white" : "text-gray-400"}`}>
                    {METHOD_LABEL_OVERRIDES[id] ?? PAYMENT_METHOD_LABELS[id]}
                  </span>
                  {idx === 0 && (
                    <div className="ml-auto w-3 h-3 rounded-full border-2 border-[#61CE70] bg-[#61CE70] flex items-center justify-center">
                      <div className="w-1 h-1 rounded-full bg-white" />
                    </div>
                  )}
                  {idx !== 0 && (
                    <div className="ml-auto w-3 h-3 rounded-full border-2 border-gray-600" />
                  )}
                </div>
              ))}
          </div>
        </div>

        <div className="mx-5 mb-4">{payButton()}</div>
        <div className="mx-5 mb-5">{thankYou()}</div>
      </>
    );
  };

  return (
    <div className="w-full">
      <div className="mx-auto max-w-[420px]">
        <div className="rounded-[2.5rem] overflow-hidden border-[6px] border-gray-700/60 shadow-2xl shadow-black/40 bg-[#090D14]">
          {/* Notch */}
          <div className="relative bg-[#090D14]">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-5 bg-[#090D14] rounded-b-2xl z-10" />
          </div>

          <div className="bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16]" style={bgStyle}>
            {/* Mini CashTap header */}
            <div className="h-12 bg-[#090D14]/80 border-b border-gray-800/50 flex items-center justify-between px-5 pt-3">
              <span className="text-[#61CE70] font-bold text-sm">CashTap</span>
              <span className="text-gray-500 text-[11px]">Powered by CashTap</span>
            </div>

            {profileContent()}

            {/* Footer */}
            <p className="text-center text-[10px] text-gray-600 pb-8">
              Payments processed securely via CashTap
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
